import Hammer from 'hammerjs';
import {parseJsonAttribute} from '../utilities/utilities';
import ViewportScaledElement from './viewport-scaled-element';
import Modal from './modal';

const settings = {
    classNames: {
        container: 'image-carousel-modal',
        loaded: 'modal--loaded',
        glint: 'modal--image-carousel--glint',
        active: 'modal--image-carousel--active',
        desktop: 'desktop-only',
        mobile: 'mobile-only',
    },

    template: `<div class="modal--image-carousel--images"></div>
    <div class="modal--image-carousel--controls modal--image-carousel--glint"></div>`,
};

/** Buttons that launch a modal containing a carousel of images */
export default class ImageCarouselModal extends ViewportScaledElement {
    constructor(openModalButton) {
        super(openModalButton);

        this.openModalButton = openModalButton;
        this.currentImageIndex = null;

        // Load our parameters
        const parameters = parseJsonAttribute(
            this.openModalButton,
            'data-carousel',
        );

        if (
            !parameters.desktop ||
            !parameters.mobile
        ) {
            throw new Error();
        }

        if (
            parameters.desktop.length <= 0 ||
            parameters.mobile.length <= 0 ||
            parameters.desktop.length != parameters.mobile.length
        ) {
            throw new Error();
        }

        // Create modal callbacks
        const navNext = (swiping = false) => {
            let toImageIndex = carousel.currentImageIndex + 1;
            if (toImageIndex >= carousel.images.length) {
                toImageIndex = 0;
            }

            carousel.navigate(toImageIndex, false, swiping);
        };

        const navPrevious = (swiping = false) => {
            let toImageIndex = carousel.currentImageIndex - 1;
            if (toImageIndex < 0) {
                toImageIndex = carousel.images.length - 1;
            }

            carousel.navigate(toImageIndex, false, swiping);
        };

        const bodyKeydownCallback = (event) => {
            switch (event.key) {
                case 'ArrowLeft':
                    navPrevious(true);
                    break;

                case 'ArrowRight':
                    navNext(true);
                    break;

                default:
                    break;
            }
        };

        /**
         * @param {Modal} modal
         * @param {Event} event
         */
        const modalPreShow = function(modal, event) {
            const imageContainer = modal.content.querySelector('.modal--image-carousel--images');
            const controlsContainer = modal.content.querySelector('.modal--image-carousel--controls');

            // Add swipe and tap navigation
            const mc = new Hammer.Manager(imageContainer);

            mc.add(
                new Hammer.Swipe({
                    // Only track horizontal swipes
                    direction: Hammer.DIRECTION_HORIZONTAL,
                }),
            );

            mc.add(
                new Hammer.Tap({
                    // Don't block repeated taps (default is 300ms)
                    interval: 0,
                }),
            );

            mc.on('swipeleft', () => {
                navNext(true);
            });

            mc.on('swiperight', () => {
                navPrevious(true);
            });

            mc.on('tap', (event) => {
                const bounds =
                    imageContainer.getBoundingClientRect();
                const percent =
                    (event.center.x - bounds.left) /
                    (bounds.right - bounds.left);

                // Note that we're treating taps like swipes in that
                // the direction of the navigation doesn't inverse
                // when looping from the first to the last slide and
                // vice versa. The buttons don't do this, intentionally
                if (percent >= 0.5) {
                    navNext(true);
                } else {
                    navPrevious(true);
                }
            });

            // Create image & button elements
            const loadingFinishedCallback = () => {
                // Add our images & buttons
                carousel.images.forEach((image) => {
                    imageContainer.appendChild(image);
                });

                carousel.buttons.forEach((button) => {
                    controlsContainer.appendChild(button);
                });

                // Perform initial navigation
                carousel.navigate(0, true);

                // Add "loaded" class after a (very) slight delay -
                // we're trying to give the layout time to size to
                // the image before the modal becomes visible, as
                // otherwise it would briefly appear too small
                setTimeout(
                    () => {
                        modal.container
                            .classList
                            .add(settings.classNames.loaded);

                        // If we opened due to a keyboard event, set focus on the close button
                        if (event instanceof KeyboardEvent) {
                            modal.closeButton.focus();
                        }
                    },
                    10,
                );

                // Remove glint after a slight delay
                setTimeout(
                    () => {
                        controlsContainer
                            .classList
                            .remove(settings.classNames.glint);
                    },
                    125,
                );
            };

            if (carousel.images && carousel.buttons) {
                // Don't wait for images to load
                loadingFinishedCallback();
            } else {
                // Detect when all images have loaded
                let loadedImageCount = 0;
                const totalImageCount = parameters
                    .desktop
                    .length;

                const loadCallback = () => {
                    loadedImageCount++;

                    if (
                        loadedImageCount <
                        totalImageCount
                    ) {
                        return;
                    }

                    // We're finished loading all the images
                    if (timeout) {
                        clearTimeout(timeout);
                        timeout = null;
                    }

                    loadingFinishedCallback();
                };

                let timeout = setTimeout(
                    () => {
                        timeout = null;
                        loadingFinishedCallback();
                    },
                    500,
                );

                // Create our slides, which triggers image loading
                createSlides(
                    carousel,
                    parameters,
                    loadCallback,
                );
            }
        };

        // Setup modal open event
        const carousel = this;
        const clickCallback = (event) => {
            // Show modal
            Modal.show(
                settings.template,
                'modal--carousel',
                Modal.presentationTypes.scaleUp,
                Modal.presentationTypes.scaleDown,
                true,
                false,
                {
                    preShow: function() {
                        const modal = this;
                        modalPreShow(modal, event);
                    },

                    postShow: function() {
                        // Add keyboard navigation
                        document.body.addEventListener('keydown', bodyKeydownCallback);
                    },

                    preClose: function() {
                        // Remove keyboard navigation
                        document.body.removeEventListener('keydown', bodyKeydownCallback);
                    },

                    postClose: function() {
                        // If we opened due to a keyboard event, set focus on the button
                        // that caused the modal to open (ideally this would be triggered
                        // based on a keyboard event when the modal is closed)
                        if (event instanceof KeyboardEvent) {
                            openModalButton.focus();
                        }
                    },
                },
                event,
            );
        };

        this.openModalButton.addEventListener(
            'keydown',
            (event) => {
                if (event.key != 'Enter') {
                    return;
                }

                clickCallback(event);
            });

        this.openModalButton.addEventListener('click', clickCallback);

        // // Testing
        // this.openModalButton.click();
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new ImageCarouselModal(elements[i]);
        }
    }

    navigate(
        toImageIndex,
        immediate = false,
        swiping = false,
    ) {
        if (
            toImageIndex < 0 ||
            toImageIndex === this.currentImageIndex ||
            toImageIndex >= this.images.length
        ) {
            return;
        }

        // Stop any ongoing animation
        if (this.navTimeline) {
            this.navTimeline.progress(1);
        }

        // Change button active states
        if (this.currentImageIndex !== null) {
            this.buttons[this.currentImageIndex]
                .classList
                .remove(settings.classNames.active);
        }

        // Set image preconditions
        this.buttons[toImageIndex]
            .classList
            .add(settings.classNames.active);

        let fromImage;
        if (this.currentImageIndex !== null) {
            fromImage = this.images[this.currentImageIndex];
            fromImage
                .classList
                .remove(settings.classNames.active);
        }

        const toImage = this.images[toImageIndex];
        toImage
            .classList
            .add(settings.classNames.active);

        if (!immediate) {
            // Animate transition
            this.navTimeline = gsap.timeline();

            // Determine if the animation should move right-to-left
            // or left-to-right
            let movingRightToLeft = toImageIndex > this.currentImageIndex;
            if (swiping === true) {
                // This navigation was initiated with a swipe - check
                // if we're transitioning from the first image to the
                // last or vice-versa
                // NOTE: we're not checking if the swipe was to the
                // left or right, so this would probably not work if
                // there were only two slides (swiping is a last-minute
                // addition...)
                if (toImageIndex == 0) {
                    // We're moving to the first image
                    if (this.currentImageIndex == this.images.length - 1) {
                        // We're moving from the last image
                        movingRightToLeft = true;
                    }
                } else if (toImageIndex == this.images.length - 1) {
                    // We're moving to the last image
                    if (this.currentImageIndex == 0) {
                        // We're moving from the first image
                        movingRightToLeft = false;
                    }
                }
            }

            if (fromImage) {
                fromImage.style.opacity = 1;
                this.navTimeline.fromTo(
                    fromImage,
                    1,
                    {
                        xPercent: 0,
                    },
                    {
                        ease: 'power3.out',
                        xPercent: movingRightToLeft ?
                            -100 :
                            100,
                    },
                    0,
                );
            }

            this.navTimeline.fromTo(
                toImage,
                1,
                {
                    xPercent: movingRightToLeft ?
                        100 :
                        -100,
                },
                {
                    ease: 'power3.out',
                    xPercent: 0,
                },
                0,
            );

            this.navTimeline.add(
                function() {
                    this.navTimeline = null;

                    if (fromImage) {
                        fromImage.removeAttribute('style');
                    }

                    toImage.removeAttribute('style');
                }.bind(this),
            );
        }

        // Update our nav index
        this.currentImageIndex = toImageIndex;
    }
}

/*
    Internal methods
*/

/**
 * Creates the slides based on the provided parameters
 * @param {ImageCarouselModal} carousel
 * @param {object} parameters
 * @param {function} loadCallback
 */
function createSlides(
    carousel,
    parameters,
    loadCallback,
) {
    carousel.images = [];
    carousel.buttons = [];

    for (let i=0; i<parameters.desktop.length; i++) {
        // Each slide is a div that contains desktop & mobile image variants
        const imageContainer = document.createElement('div');

        const desktopImage = new Image();
        desktopImage.className = settings.classNames.desktop;
        desktopImage.src = parameters.desktop[i];
        desktopImage.onload = loadCallback;
        imageContainer.appendChild(desktopImage);

        const mobileImage = new Image();
        mobileImage.className = settings.classNames.mobile;
        mobileImage.src = parameters.mobile[i];
        mobileImage.onload = loadCallback;
        imageContainer.appendChild(mobileImage);

        carousel.images.push(imageContainer);

        // Create the button associated with this slide
        const button = document.createElement('button');
        button.addEventListener(
            'click',
            (event) => {
                event.preventDefault();
                event.stopPropagation();

                carousel.navigate(i);
            },
        );

        carousel.buttons.push(button);
    }
}
